import { useState, useEffect } from "react";
import axios from "axios";
import { Moon, Sun, TrendingUp, TrendingDown, Clock } from "lucide-react";
import { API } from "../config/api";

const ExtendedHoursData = ({ ticker }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ticker) return;

    const fetchExtendedHours = async () => {
      setLoading(true);
      try {
        const response = await axios.post(`${API}/stock/info`, { ticker });
        setData(response.data);
      } catch (error) {
        console.error("Error fetching extended hours data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchExtendedHours();
    
    // Refresh every 60 seconds
    const interval = setInterval(fetchExtendedHours, 60000);
    return () => clearInterval(interval);
  }, [ticker]);

  const formatPercent = (value) => {
    if (value === null || value === undefined) return null;
    const sign = value >= 0 ? "+" : "";
    return `${sign}${Number(value).toFixed(2)}%`;
  };

  const formatPrice = (value) => {
    if (value === null || value === undefined) return null;
    return `$${Number(value).toFixed(2)}`;
  };

  // Check if we have extended hours data
  const hasPreMarket = data?.preMarketPrice && data?.preMarketPrice > 0;
  const hasAfterHours = data?.postMarketPrice && data?.postMarketPrice > 0;
  
  if (loading || (!hasPreMarket && !hasAfterHours)) {
    return null; // Don't show if no extended hours data
  }

  const preMarketChange = data?.preMarketChangePercent;
  const afterHoursChange = data?.postMarketChangePercent;

  return (
    <div className="bg-gradient-to-r from-[#1a1a3e] to-[#1e1e2f] border border-indigo-500/30 rounded-lg" data-testid="extended-hours-card">
      <div className="py-3 px-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          {/* Header */}
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-indigo-400" />
            <span className="text-sm font-semibold text-white">Extended Hours Trading</span>
          </div>

          {/* Extended Hours Data */}
          <div className="flex items-center gap-6">
            {/* Pre-Market */}
            {hasPreMarket && (
              <div className="flex items-center gap-2 bg-yellow-900/20 px-3 py-1.5 rounded-lg border border-yellow-500/30">
                <Sun className="w-4 h-4 text-yellow-400" />
                <span className="text-sm text-yellow-200 font-medium">Pre-Market:</span>
                <span className="text-sm font-bold text-white">
                  {formatPrice(data.preMarketPrice)}
                </span>
                <span className={`text-sm font-bold flex items-center gap-1 ${
                  preMarketChange >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {preMarketChange >= 0 ? (
                    <TrendingUp className="w-3.5 h-3.5" />
                  ) : (
                    <TrendingDown className="w-3.5 h-3.5" />
                  )}
                  {formatPercent(preMarketChange)}
                </span>
              </div>
            )}

            {/* After-Hours */}
            {hasAfterHours && (
              <div className="flex items-center gap-2 bg-blue-900/20 px-3 py-1.5 rounded-lg border border-blue-500/30">
                <Moon className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-blue-200 font-medium">After-Hours:</span>
                <span className="text-sm font-bold text-white">
                  {formatPrice(data.postMarketPrice)}
                </span>
                <span className={`text-sm font-bold flex items-center gap-1 ${
                  afterHoursChange >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {afterHoursChange >= 0 ? (
                    <TrendingUp className="w-3.5 h-3.5" />
                  ) : (
                    <TrendingDown className="w-3.5 h-3.5" />
                  )}
                  {formatPercent(afterHoursChange)}
                </span>
              </div>
            )}
          </div>

          {/* Regular Market Reference */}
          <div className="text-xs text-gray-300 bg-gray-800/50 px-2 py-1 rounded">
            <span className="text-gray-400">Close:</span>{' '}
            <span className="text-white font-medium">{formatPrice(data?.regularMarketPrice)}</span>{' '}
            <span className={data?.regularMarketChangePercent >= 0 ? 'text-green-400' : 'text-red-400'}>
              ({formatPercent(data?.regularMarketChangePercent)})
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtendedHoursData;
